import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import PublicAccountSummary from "./PublicAccountSummary";
import { PUBLIC_SESSION_CONFIG, getPublicSession, getPublicSessionPayload, isPublicSessionError, logoutPublicSession } from "./publicAccountSession";

const PRODUCTS_URL = "https://www.articulate.com.sg/products";

const TYPE_OF_WRITING_OPTIONS = [
  { value: "composition", label: "Composition" },
  { value: "situational writing", label: "Situational Writing" },
];

const EXAM_LEVEL_OPTIONS = [
  { value: "psle", label: "PSLE" },
  { value: "o-level", label: "O-Level" },
  { value: "na-level", label: "NA-Level" },
  { value: "nt-level", label: "NT-Level" },
];

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const labelStyle = {
  fontWeight: 700,
  marginBottom: 6,
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  fontSize: 14,
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "10px 16px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontWeight: 700,
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#3F51B5",
  color: "#ffffff",
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: "#e5e7eb",
  color: "#111827",
};

const disabledButtonStyle = {
  ...buttonStyle,
  background: "#d1d5db",
  color: "#6b7280",
  cursor: "not-allowed",
};

function getResponsePayload(rawData = {}) {
  if (
    rawData?.data &&
    typeof rawData.data === "object" &&
    !Array.isArray(rawData.data)
  ) {
    return {
      ...rawData,
      ...rawData.data,
    };
  }

  return rawData || {};
}

function getErrorPayload(err) {
  return getResponsePayload(err?.response?.data || {});
}

function isInsufficientCreditPayload(status, data = {}) {
  const reason = data?.reason || "";
  const error = data?.error || "";
  const errorCode = data?.error_code || data?.code || "";

  return (
    status === 402 ||
    error === "insufficient_credits" ||
    errorCode === "INSUFFICIENT_CREDITS" ||
    reason === "insufficient_credit_or_subscription"
  );
}

function getJobStatusBadge(jobStatus) {
  const status = (jobStatus || "").toLowerCase();

  if (status === "complete" || status === "completed") {
    return {
      label: "Complete",
      bg: "#e7f7ed",
      fg: "#136f3a",
      border: "#bfe8cf",
    };
  }

  if (status === "failed" || status === "error") {
    return {
      label: status === "failed" ? "Failed" : "Error",
      bg: "#fdecec",
      fg: "#b42318",
      border: "#f9c7c7",
    };
  }

  if (status === "queued") {
    return {
      label: "Queued",
      bg: "#eef2ff",
      fg: "#3730a3",
      border: "#c7d2fe",
    };
  }

  return {
    label: status ? "Processing" : "-",
    bg: "#fff7e6",
    fg: "#8a4b00",
    border: "#ffe0b2",
  };
}

function getReportUrl(result) {
  return (
    result?.html_report_url ||
    result?.html_report_sas_url ||
    result?.report_url ||
    ""
  ).trim();
}

function PublicWritingGraderPage() {
  const [checkingPublicSession, setCheckingPublicSession] = useState(true);
  const [publicSessionError, setPublicSessionError] = useState("");
  const [publicUser, setPublicUser] = useState(null);
  const [publicBalances, setPublicBalances] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [publicEmail, setPublicEmail] = useState("");
  const [publicEmailCode, setPublicEmailCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState("");
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [verifyingEmailCode, setVerifyingEmailCode] = useState(false);
  const [emailSendCooldown, setEmailSendCooldown] = useState(0);
  const [userId, setUserId] = useState("");
  const [userEmail, setUserEmail] = useState("");

  const [typeOfWriting, setTypeOfWriting] = useState("composition");
  const [examLevel, setExamLevel] = useState("psle");

  const [questionFile, setQuestionFile] = useState(null);
  const [questionStatus, setQuestionStatus] = useState("");
  const [questionJobId, setQuestionJobId] = useState("");
  const [questionJobStatus, setQuestionJobStatus] = useState("");
  const [questionComplete, setQuestionComplete] = useState(false);
  const [questionUploading, setQuestionUploading] = useState(false);

  const [answerUploadMode, setAnswerUploadMode] = useState("pdf");
  const [answerFile, setAnswerFile] = useState(null);
  const [answerFiles, setAnswerFiles] = useState([]);
  const [answerStatus, setAnswerStatus] = useState("");
  const [answerJobId, setAnswerJobId] = useState("");
  const [answerJobStatus, setAnswerJobStatus] = useState("");
  const [answerUploading, setAnswerUploading] = useState(false);
  const [reportUrl, setReportUrl] = useState("");

  const [accessNeededDetails, setAccessNeededDetails] = useState(null);

  const questionPollingRef = useRef(null);
  const answerPollingRef = useRef(null);

  const clearPublicAuthentication = (message = "") => {
    setPublicUser(null); setPublicBalances(null); setEmailVerified(false);
    setUserId(""); setUserEmail(""); setPublicEmailCode(""); setEmailCodeSent(false);
    setEmailVerificationStatus(message);
  };

  const applyPublicSession = ({ user, balances }) => {
    const restoredUserId = user?.user_id || "";
    const restoredEmail = user?.masked_email || "";
    setPublicUser(user); setPublicBalances(balances); setUserId(restoredUserId);
    setUserEmail(restoredEmail); setPublicEmail(restoredEmail);
    setEmailVerified(Boolean(restoredUserId)); setEmailVerificationStatus("Account session restored.");
  };

  const checkPublicSession = async () => {
    setCheckingPublicSession(true); setPublicSessionError("");
    try {
      const session = getPublicSessionPayload(await getPublicSession());
      if (session.authenticated) applyPublicSession(session); else clearPublicAuthentication();
    } catch (_) { setPublicSessionError("We could not check your account. Please try again."); }
    finally { setCheckingPublicSession(false); }
  };

  const clearQuestionPolling = () => {
    if (questionPollingRef.current) {
      clearInterval(questionPollingRef.current);
      questionPollingRef.current = null;
    }
  };

  const clearAnswerPolling = () => {
    if (answerPollingRef.current) {
      clearInterval(answerPollingRef.current);
      answerPollingRef.current = null;
    }
  };

  const showInsufficientCredit = (details = {}) => {
    setAccessNeededDetails({
      message:
        details?.message ||
        details?.error_message ||
        "You do not have enough credits or an active subscription to continue.",
      payment_options: details?.payment_options || null,
      credit_balance: details?.credit_balance,
      reason:
        details?.reason ||
        details?.error ||
        details?.error_code ||
        "insufficient_credit_or_subscription",
    });
  };

  const handleUploadError = (err, setStatus) => {
    const data = getErrorPayload(err);
    if (isInsufficientCreditPayload(err?.response?.status, data)) {
      showInsufficientCredit(data);
      setStatus("Writing grader access needed.");
      return;
    }
    if (isPublicSessionError(err)) {
      clearPublicAuthentication("Your session has expired. Please verify your email again.");
      setStatus("Your session has expired. Please verify your email again.");
      return;
    }

    setStatus(
      data?.message ||
        data?.error ||
        err?.message ||
        "Upload failed."
    );
  };

  const startPollingJobStatus = (jobId, kind) => {
    const isQuestion = kind === "question";
    const clearPolling = isQuestion ? clearQuestionPolling : clearAnswerPolling;
    const setJobStatus = isQuestion ? setQuestionJobStatus : setAnswerJobStatus;
    const setStatus = isQuestion ? setQuestionStatus : setAnswerStatus;
    const pollingRef = isQuestion ? questionPollingRef : answerPollingRef;

    clearPolling();
    setJobStatus("queued");

    pollingRef.current = setInterval(async () => {
      try {
        const res = await axios.get("/api/check-job-status", {
          params: { job_id: jobId },
          ...PUBLIC_SESSION_CONFIG,
        });

        const payload = getResponsePayload(res.data || {});
        const status = payload?.status || "processing";
        const normalizedStatus = String(status).toLowerCase();

        setJobStatus(normalizedStatus);

        if (normalizedStatus === "complete" || normalizedStatus === "completed") {
          clearPolling();

          const result = payload?.result || null;

          if (isQuestion) {
            setQuestionComplete(true);
            setStatus("Question upload complete. You can now upload the student's answer.");
          } else {
            const url = getReportUrl(result);
            setReportUrl(url);
            setStatus(url ? "Answer grading complete. Report ready." : "Answer grading complete.");
            getPublicSession().then((response) => {
              const session = getPublicSessionPayload(response);
              if (session.authenticated) setPublicBalances(session.balances);
            }).catch(() => {});
          }
        } else if (normalizedStatus === "failed" || normalizedStatus === "error") {
          clearPolling();
          setStatus(payload?.message || payload?.error || `Job ${normalizedStatus}.`);
        } else {
          setStatus(isQuestion ? "Processing question..." : "Grading answer...");
        }
      } catch (err) {
        const data = getErrorPayload(err);
        if (isInsufficientCreditPayload(err?.response?.status, data)) {
          clearPolling();
          showInsufficientCredit(data);
          setStatus("Writing grader access needed.");
          return;
        }
        if (isPublicSessionError(err)) {
          clearPolling();
          clearPublicAuthentication("Your session has expired. Please verify your email again.");
          setStatus("Your session has expired. Please verify your email again.");
          return;
        }

        setStatus("Polling... temporary connection issue.");
      }
    }, 2000);
  };

  const handleSendEmailCode = async () => {
    const normalizedEmail = publicEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setEmailVerificationStatus("Please enter your email address.");
      return;
    }

    setSendingEmailCode(true);
    setEmailVerified(false);
    setUserId("");
    setUserEmail("");
    setAccessNeededDetails(null);
    setEmailVerificationStatus("Sending email verification code...");

    try {
      await axios.post("/api/public/oral/send-email-code", {
        email: normalizedEmail,
      });
      setEmailCodeSent(true);
      setEmailSendCooldown(30);
      setEmailVerificationStatus(`Verification code sent to ${normalizedEmail}.`);
    } catch (err) {
      const data = getErrorPayload(err);
      setEmailVerificationStatus(
        data?.error ||
          data?.message ||
          err?.message ||
          "Failed to send email verification code."
      );
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    const normalizedEmail = publicEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setEmailVerificationStatus("Please enter your email address.");
      return;
    }

    if (!publicEmailCode.trim()) {
      setEmailVerificationStatus("Please enter the email verification code.");
      return;
    }

    setVerifyingEmailCode(true);
    setEmailVerificationStatus("Verifying email code...");

    try {
      const res = await axios.post("/api/public/oral/verify-email-code", {
        email: normalizedEmail,
        code: publicEmailCode.trim(),
        marketing_email_consent: false,
      }, PUBLIC_SESSION_CONFIG);

      const payload = getResponsePayload(res.data || {});
      const verifiedUserId = payload?.user_id || "";
      const verifiedEmail = payload?.user_email || normalizedEmail;

      if (!verifiedUserId) {
        throw new Error("Email verified, but no user ID was returned.");
      }

      setUserId(verifiedUserId);
      setUserEmail(verifiedEmail);
      setPublicEmail(verifiedEmail);
      setEmailVerified(true);
      setEmailVerificationStatus("Email verified. Upload the question PDF first.");
      const session = getPublicSessionPayload(await getPublicSession());
      if (session.authenticated) applyPublicSession(session);
    } catch (err) {
      const data = getErrorPayload(err);
      setEmailVerified(false);
      setUserId("");
      setUserEmail("");
      setEmailVerificationStatus(
        data?.error ||
          data?.message ||
          err?.message ||
          "Failed to verify email code."
      );
    } finally {
      setVerifyingEmailCode(false);
    }
  };

  const buildSharedUploadFormData = () => {
    const formData = new FormData();
    formData.append("email", userEmail || publicEmail.trim().toLowerCase());
    formData.append("user_email", userEmail || publicEmail.trim().toLowerCase());
    formData.append("user_id", userId);
    formData.append("type_of_writing", typeOfWriting);
    formData.append("exam_level", examLevel);
    return formData;
  };

  const validateVerifiedUser = () => {
    if (!emailVerified || !userId || !(userEmail || publicEmail).trim()) {
      return "Please verify your email address first.";
    }
    return "";
  };

  const handleUploadQuestion = async (e) => {
    e.preventDefault();
    setQuestionStatus("");
    setQuestionJobId("");
    setQuestionJobStatus("");
    setQuestionComplete(false);
    setAccessNeededDetails(null);
    clearQuestionPolling();

    const verifiedError = validateVerifiedUser();
    if (verifiedError) {
      setQuestionStatus(verifiedError);
      return;
    }

    if (!typeOfWriting || !examLevel) {
      setQuestionStatus("Please select type of writing and exam level.");
      return;
    }

    if (!questionFile) {
      setQuestionStatus("Please choose a question PDF first.");
      return;
    }

    try {
      setQuestionUploading(true);
      setQuestionStatus("Uploading question PDF...");

      const formData = buildSharedUploadFormData();
      formData.append("file", questionFile);

      const res = await axios.post("/api/public-writing/upload-question", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        ...PUBLIC_SESSION_CONFIG,
      });

      const returnedJobId = res.data?.job_id || res.data?.data?.job_id || "";
      if (!returnedJobId) {
        setQuestionStatus(res.data?.message || "Question uploaded, but no job ID was returned.");
        return;
      }

      setQuestionJobId(returnedJobId);
      setQuestionStatus("Question queued. Processing...");
      startPollingJobStatus(returnedJobId, "question");
    } catch (err) {
      handleUploadError(err, setQuestionStatus);
    } finally {
      setQuestionUploading(false);
    }
  };

  const handleUploadAnswer = async (e) => {
    e.preventDefault();
    setAnswerStatus("");
    setAnswerJobId("");
    setAnswerJobStatus("");
    setReportUrl("");
    setAccessNeededDetails(null);
    clearAnswerPolling();

    const verifiedError = validateVerifiedUser();
    if (verifiedError) {
      setAnswerStatus(verifiedError);
      return;
    }

    if (!questionComplete) {
      setAnswerStatus("Upload and process the question PDF before uploading the answer.");
      return;
    }

    if (answerUploadMode === "pdf" && !answerFile) {
      setAnswerStatus("Please choose an answer PDF first.");
      return;
    }

    if (answerUploadMode === "images" && answerFiles.length === 0) {
      setAnswerStatus("Please choose at least one answer image.");
      return;
    }

    try {
      setAnswerUploading(true);
      setAnswerStatus(answerUploadMode === "pdf" ? "Uploading answer PDF..." : "Uploading answer images...");

      const formData = buildSharedUploadFormData();
      let endpoint = "/api/public-writing/upload-answer";

      if (answerUploadMode === "pdf") {
        formData.append("file", answerFile);
      } else {
        endpoint = "/api/public-writing/upload-answer-images";
        answerFiles.forEach((file) => formData.append("files", file));
      }

      const res = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        ...PUBLIC_SESSION_CONFIG,
      });

      const returnedJobId = res.data?.job_id || res.data?.data?.job_id || "";
      if (!returnedJobId) {
        setAnswerStatus(res.data?.message || "Answer uploaded, but no job ID was returned.");
        return;
      }

      setAnswerJobId(returnedJobId);
      setAnswerStatus("Answer queued. Grading...");
      startPollingJobStatus(returnedJobId, "answer");
    } catch (err) {
      handleUploadError(err, setAnswerStatus);
    } finally {
      setAnswerUploading(false);
    }
  };

  const handleReset = () => {
    clearQuestionPolling();
    clearAnswerPolling();
    setQuestionFile(null);
    setQuestionStatus("");
    setQuestionJobId("");
    setQuestionJobStatus("");
    setQuestionComplete(false);
    setAnswerUploadMode("pdf");
    setAnswerFile(null);
    setAnswerFiles([]);
    setAnswerStatus("");
    setAnswerJobId("");
    setAnswerJobStatus("");
    setReportUrl("");
    setAccessNeededDetails(null);
  };

  const handlePublicLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutPublicSession();
      handleReset(); clearPublicAuthentication(); setPublicEmail("");
    } catch (_) { setEmailVerificationStatus("Logout failed. Please try again."); }
    finally { setLoggingOut(false); }
  };

  useEffect(() => {
    checkPublicSession();
    // Session restoration is a one-time page bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (emailSendCooldown <= 0) return undefined;

    const timer = setTimeout(() => {
      setEmailSendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [emailSendCooldown]);

  useEffect(() => {
    return () => {
      clearQuestionPolling();
      clearAnswerPolling();
    };
  }, []);

  const selectorsLocked = questionComplete || Boolean(questionJobId);
  const canUploadQuestion =
    emailVerified && userId && questionFile && !questionUploading && !questionJobId;
  const canUploadAnswer =
    emailVerified &&
    userId &&
    questionComplete &&
    !answerUploading &&
    !answerJobId &&
    (answerUploadMode === "pdf" ? Boolean(answerFile) : answerFiles.length > 0);

  const questionBadge = getJobStatusBadge(questionJobStatus);
  const answerBadge = getJobStatusBadge(answerJobStatus);
  const paymentOptions = accessNeededDetails?.payment_options || {};
  const subscriptionPaymentUrl = paymentOptions?.subscription?.url || PRODUCTS_URL;
  const creditPaymentUrl = paymentOptions?.credits?.url || PRODUCTS_URL;
  const hasCreditPaymentOption = Boolean(paymentOptions?.credits);

  if (checkingPublicSession) return <div style={{ padding: 24 }}>Checking your account…</div>;
  if (publicSessionError) return <div style={{ padding: 24 }}><p>{publicSessionError}</p><button type="button" onClick={checkPublicSession}>Retry</button></div>;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 16, color: "#111827" }}>
      <h2 style={{ marginBottom: 8 }}>Writing Grader</h2>
      <p style={{ marginTop: 0, color: "#4b5563" }}>
        Verify your email, upload the question PDF, then upload the student's answer for grading.
      </p>
      <PublicAccountSummary user={publicUser} balances={publicBalances} onLogout={handlePublicLogout} loggingOut={loggingOut} />

      <div style={{ display: "grid", gap: 16 }}>
        {!emailVerified ? <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Verify Your Email</h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            <div>
              <label style={labelStyle}>Email Address</label>
              <input
                style={inputStyle}
                type="email"
                value={publicEmail}
                onChange={(e) => setPublicEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={questionUploading || answerUploading}
              />
            </div>

            <div>
              <label style={labelStyle}>Email Verification Code</label>
              <input
                style={inputStyle}
                value={publicEmailCode}
                onChange={(e) => setPublicEmailCode(e.target.value)}
                placeholder="Enter email code"
                disabled={questionUploading || answerUploading}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              marginTop: 12,
            }}
          >
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={handleSendEmailCode}
              disabled={sendingEmailCode || emailSendCooldown > 0 || questionUploading || answerUploading}
            >
              {emailSendCooldown > 0
                ? `Send Again (${emailSendCooldown}s)`
                : sendingEmailCode
                ? "Sending..."
                : "Send Email Code"}
            </button>

            <button
              type="button"
              style={primaryButtonStyle}
              onClick={handleVerifyEmailCode}
              disabled={
                !publicEmail.trim() ||
                !publicEmailCode.trim() ||
                verifyingEmailCode ||
                questionUploading ||
                answerUploading
              }
            >
              {verifyingEmailCode ? "Verifying..." : "Verify Email"}
            </button>

            <div style={{ color: emailVerified ? "#166534" : "#6b7280", fontSize: 14 }}>
              {emailVerificationStatus ||
                (emailCodeSent
                  ? "Enter the code from your email to continue."
                  : "Send an email code before uploading.")}
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <a href="/public-dashboard-embed" target="_blank" rel="noopener noreferrer">View My Account</a>
          </div>
        </section> : null}

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Setup</h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
            }}
          >
            <div>
              <label style={labelStyle}>Type of Writing</label>
              <select
                style={inputStyle}
                value={typeOfWriting}
                onChange={(e) => setTypeOfWriting(e.target.value)}
                disabled={selectorsLocked}
              >
                {TYPE_OF_WRITING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Exam Level</label>
              <select
                style={inputStyle}
                value={examLevel}
                onChange={(e) => setExamLevel(e.target.value)}
                disabled={selectorsLocked}
              >
                {EXAM_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectorsLocked ? (
            <div style={{ marginTop: 10, color: "#6b7280", fontSize: 14 }}>
              Type of writing and exam level are locked after question upload starts. Use Reset to change them.
            </div>
          ) : null}

          <button type="button" style={{ ...secondaryButtonStyle, marginTop: 12 }} onClick={handleReset}>
            Reset
          </button>
        </section>

        {accessNeededDetails ? (
          <section
            style={{
              background: "#fff7ed",
              border: "1px solid #fdba74",
              borderRadius: 12,
              color: "#9a3412",
              padding: 16,
              fontSize: 14,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 18 }}>Writing Grader Access Needed</div>
            <div style={{ marginTop: 8, lineHeight: 1.5 }}>{accessNeededDetails.message}</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              <button
                type="button"
                style={primaryButtonStyle}
                onClick={() => window.open(subscriptionPaymentUrl, "_blank", "noopener,noreferrer")}
              >
                View Plans and Credits
              </button>
              {hasCreditPaymentOption ? (
                <button
                  type="button"
                  style={secondaryButtonStyle}
                  onClick={() => window.open(creditPaymentUrl, "_blank", "noopener,noreferrer")}
                >
                  Top Up Credits
                </button>
              ) : null}
            </div>
          </section>
        ) : null}

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>1. Upload Question PDF</h3>

          <form onSubmit={handleUploadQuestion} style={{ display: "grid", gap: 12 }}>
            <div>
              <label style={labelStyle}>Question PDF</label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setQuestionFile(e.target.files?.[0] || null)}
                disabled={!emailVerified || Boolean(questionJobId)}
              />
            </div>

            <button
              type="submit"
              style={canUploadQuestion ? primaryButtonStyle : disabledButtonStyle}
              disabled={!canUploadQuestion}
            >
              {questionUploading ? "Uploading..." : "Upload Question"}
            </button>
          </form>

          <JobStatusBlock
            status={questionStatus}
            jobId={questionJobId}
            badge={questionBadge}
            jobStatus={questionJobStatus}
          />
        </section>

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>2. Upload Student Answer</h3>

          {!questionComplete ? (
            <div style={{ marginBottom: 12, color: "#6b7280" }}>
              Answer upload will be enabled after the question PDF finishes processing.
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              style={answerUploadMode === "pdf" ? primaryButtonStyle : secondaryButtonStyle}
              onClick={() => {
                setAnswerUploadMode("pdf");
                setAnswerFiles([]);
              }}
              disabled={!questionComplete || Boolean(answerJobId)}
            >
              PDF Upload
            </button>
            <button
              type="button"
              style={answerUploadMode === "images" ? primaryButtonStyle : secondaryButtonStyle}
              onClick={() => {
                setAnswerUploadMode("images");
                setAnswerFile(null);
              }}
              disabled={!questionComplete || Boolean(answerJobId)}
            >
              Image Upload
            </button>
          </div>

          <form onSubmit={handleUploadAnswer} style={{ display: "grid", gap: 12 }}>
            {answerUploadMode === "pdf" ? (
              <div>
                <label style={labelStyle}>Answer PDF</label>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => {
                    setAnswerFile(e.target.files?.[0] || null);
                    setAnswerFiles([]);
                  }}
                  disabled={!questionComplete || Boolean(answerJobId)}
                />
              </div>
            ) : (
              <div>
                <label style={labelStyle}>Answer Images</label>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    setAnswerFiles(Array.from(e.target.files || []));
                    setAnswerFile(null);
                  }}
                  disabled={!questionComplete || Boolean(answerJobId)}
                />
                <div style={{ marginTop: 6, color: "#6b7280", fontSize: 13 }}>
                  Accepted formats: JPG or PNG.
                </div>
                {answerFiles.length > 0 ? (
                  <ol style={{ marginTop: 8 }}>
                    {answerFiles.map((file, index) => (
                      <li key={`${file.name}-${index}`}>{file.name}</li>
                    ))}
                  </ol>
                ) : null}
              </div>
            )}

            <button
              type="submit"
              style={canUploadAnswer ? primaryButtonStyle : disabledButtonStyle}
              disabled={!canUploadAnswer}
            >
              {answerUploading
                ? "Uploading..."
                : answerUploadMode === "pdf"
                ? "Upload Answer PDF"
                : "Upload Answer Images"}
            </button>
          </form>

          <JobStatusBlock
            status={answerStatus}
            jobId={answerJobId}
            badge={answerBadge}
            jobStatus={answerJobStatus}
          />

          {reportUrl ? (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                style={primaryButtonStyle}
                onClick={() => window.open(reportUrl, "_blank", "noopener,noreferrer")}
              >
                Open Report
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function JobStatusBlock({ status, jobId, badge, jobStatus }) {
  if (!status && !jobId) return null;

  return (
    <div
      style={{
        marginTop: 12,
        display: "grid",
        gap: 8,
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 12,
        fontSize: 14,
      }}
    >
      <div>
        <b>Status:</b> {status || "-"}
      </div>
      <div>
        <b>Job ID:</b> {jobId || "-"}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <b>Job Status:</b>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 700,
            background: badge.bg,
            color: badge.fg,
            border: `1px solid ${badge.border}`,
          }}
        >
          {badge.label || jobStatus || "-"}
        </span>
      </div>
    </div>
  );
}

export default PublicWritingGraderPage;
